import { useEffect } from 'react';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import useCommentDocuments from '../hooks/useCommentDocuments';
import { getDocId, getDocumentFileName } from '../utils/documentUtils';

const ACCEPTED_FILE_TYPES =
    'image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/octet-stream,application/zip,application/vnd.rar,application/x-rar-compressed,.pdf,.doc,.docx,.xls,.xlsx,.txt,.trc,.zip,.rar';

const CommentDocuments = ({ commentId, isCommentOwner, isLastUserMessage, onDocumentsChange, onDownloadDocument, onDeleteDocument, onUploadDocument, isUploading, disabled, th }) => {
    const { documents, documentIds, isLoading } = useCommentDocuments(commentId);
    const canEditDocuments = isCommentOwner && isLastUserMessage && !disabled;

    useEffect(() => {
        onDocumentsChange(commentId, { documentIds, isLoading });
    }, [commentId, documentIds, isLoading, onDocumentsChange]);

    if (isLoading) return <div className="text-xs text-500 mt-2">{th('common.loading')}...</div>;
    if (documents.length === 0 && !canEditDocuments) return null;

    return (
        <div className="mt-2 pt-2 border-top-1 border-200">
            <div className="text-xs text-600 mb-2">{th('technicalSupport.attachedFiles')}:</div>
            {documents.length > 0 && (
                <div className="flex flex-column gap-1">
                    {documents.map((document) => {
                        const documentId = getDocId(document);
                        const fileName = getDocumentFileName(document);

                        return (
                            <div key={documentId ?? fileName} className="flex align-items-center gap-2 text-xs">
                                <i className="pi pi-paperclip text-500" />
                                <span className="flex-1 text-700">{fileName}</span>
                                <Button
                                    icon="pi pi-download"
                                    className="p-button-text p-button-sm p-0"
                                    style={{ height: '1.5rem', width: '1.5rem' }}
                                    onClick={() => onDownloadDocument(document)}
                                    tooltip={th('common.download')}
                                    tooltipOptions={{ position: 'top' }}
                                />
                                {canEditDocuments && (
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-text p-button-danger p-button-sm p-0"
                                        style={{ height: '1.5rem', width: '1.5rem' }}
                                        onClick={() => onDeleteDocument(commentId, documentId, fileName, isCommentOwner)}
                                        tooltip={th('common.delete')}
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {canEditDocuments && (
                <div className="flex justify-content-end mt-2">
                    <FileUpload
                        mode="basic"
                        name="file"
                        accept={ACCEPTED_FILE_TYPES}
                        maxFileSize={104857600}
                        chooseLabel={th('technicalSupport.attachFile')}
                        chooseOptions={{ icon: 'pi pi-paperclip', className: 'p-button-text p-button-sm p-0' }}
                        multiple
                        customUpload
                        auto
                        uploadHandler={(event) => onUploadDocument(commentId, event.files)}
                        disabled={isUploading}
                    />
                </div>
            )}
        </div>
    );
};

export default CommentDocuments;
